import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { attachForegroundFcm } from "../services/firebaseMessagingService";
import { handleFcmNotificationMessage } from "../services/fcmNotificationHandler";
import { getAllEWalletsAmount } from "../utils/apiClient";
import { syncWalletsAfterNotification } from "../utils/walletSync";
import { playNotificationRingtone, unlockNotificationSound } from "../utils/notificationSound";

const isSignedIn = () => {
  const t = localStorage.getItem("token") || sessionStorage.getItem("token");
  return !!(t && t !== "FIRST_TIME_LOGIN" && String(t).trim() !== "");
};

const BADGE_POLL_MS = 20000;

/**
 * Subscribes to foreground FCM, service-worker notification clicks, and syncs
 * the header notification badge (mirrors Flutter FCM → updateEWalletsAmount).
 */
export default function FcmNotificationListener() {
  const navigate = useNavigate();

  useEffect(() => {
    unlockNotificationSound();
  }, []);

  useEffect(() => {
    let detach = () => {};
    let pollTimer = null;
    let lastNotificationCount = null;
    let pollingStopped = false;

    (async () => {
      detach = await attachForegroundFcm(navigate, isSignedIn);
    })();

    const onSwMessage = (event) => {
      if (!isSignedIn()) return;
      const msg = event?.data;
      if (!msg || msg.type !== "MFG_FCM_ACTION" || !msg.payload) return;
      handleFcmNotificationMessage(navigate, msg.payload, false);
    };

    const consumeInitialSwPayload = () => {
      if (!isSignedIn()) return;
      try {
        const url = new URL(window.location.href);
        const encoded = url.searchParams.get("mfg_fcm_payload");
        if (!encoded) return;
        let payload = null;
        try {
          payload = JSON.parse(decodeURIComponent(encoded));
        } catch (_) {
          payload = null;
        }
        if (payload) {
          handleFcmNotificationMessage(navigate, payload, false);
        }
        url.searchParams.delete("mfg_fcm_payload");
        window.history.replaceState({}, "", `${url.pathname}${url.search}${url.hash}`);
      } catch (_) {}
    };

    try {
      navigator.serviceWorker?.addEventListener?.("message", onSwMessage);
    } catch (_) {}
    consumeInitialSwPayload();

    const syncNotificationBadge = async ({ toastOnIncrease = true } = {}) => {
      if (pollingStopped || !isSignedIn()) return;
      if (typeof document !== "undefined" && document.visibilityState === "hidden") return;
      try {
        const response = await getAllEWalletsAmount();
        const data = response?.decryptedData || response?.data || {};
        if (Number(data?.CODE) !== 200 || !data?.NOTIFICATIONS) return;
        const count = Math.max(0, Number(data.NOTIFICATIONS.NOTIFICATIONS) || 0);
        const chatCount = Math.max(0, Number(data.NOTIFICATIONS.CHAT_NOTIFICATIONS) || 0);
        try {
          window.dispatchEvent(
            new CustomEvent("mfg:notification-badge", {
              detail: { count },
            })
          );
        } catch (_) {}
        try {
          window.dispatchEvent(
            new CustomEvent("mfg:chat-notification-badge", {
              detail: { count: chatCount },
            })
          );
        } catch (_) {}

        // First poll just initializes baseline to avoid false-positive toast.
        if (lastNotificationCount == null) {
          lastNotificationCount = count;
          return;
        }

        if (toastOnIncrease && count > lastNotificationCount) {
          playNotificationRingtone();
          try {
            window.dispatchEvent(
              new CustomEvent("mfg:fcm-foreground-toast", {
                detail: {
                  title: "Notification",
                  body: "You have a new notification.",
                },
              })
            );
          } catch (_) {}
        }
        lastNotificationCount = count;
      } catch (_) {}
    };

    syncNotificationBadge({ toastOnIncrease: false });
    pollTimer = window.setInterval(() => syncNotificationBadge(), BADGE_POLL_MS);

    // When an FCM notification arrives, refresh badge from server (like chat Pusher → fetch)
    const onHeaderNotification = () => {
      syncNotificationBadge({ toastOnIncrease: false });
    };
    window.addEventListener("mfg:header-notification", onHeaderNotification);

    // Mirror Flutter HomeScreen.didChangeAppLifecycleState(resumed) → updateEWalletsAmount.
    let skipFirstResume = true;
    const onVisibilityChange = () => {
      if (pollingStopped || !isSignedIn()) return;
      if (typeof document === "undefined" || document.visibilityState !== "visible") return;
      if (skipFirstResume) {
        skipFirstResume = false;
        return;
      }
      syncWalletsAfterNotification({ title: "app_resume" });
      syncNotificationBadge({ toastOnIncrease: false });
    };
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      pollingStopped = true;
      if (pollTimer) window.clearInterval(pollTimer);
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("mfg:header-notification", onHeaderNotification);
      if (typeof detach === "function") detach();
      try {
        navigator.serviceWorker?.removeEventListener?.("message", onSwMessage);
      } catch (_) {}
    };
  }, [navigate]);

  return null;
}
